<template>
  <div class="v5075-panel" v-loading="loading">
    <section class="panel-header">
      <div>
        <h3>Ford V5075 开关配置</h3>
        <p>Redis Key：FordServer:V5075:*</p>
      </div>

      <el-button :icon="Refresh" :loading="loading" plain @click="fetchConfig">
        刷新
      </el-button>
    </section>

    <section class="config-section">
      <div class="section-heading">
        <div>
          <h4>新结构请求模式</h4>
          <p>FordServer:V5075:NewCurlMode</p>
        </div>
        <el-tag effect="plain">{{ modeDefaultText }}</el-tag>
      </div>

      <el-radio-group v-model="mode" class="mode-group" :disabled="savingMode">
        <el-radio-button
          v-for="item in modeOptions"
          :key="item.value"
          :label="item.value"
        >
          {{ item.label }}
        </el-radio-button>
      </el-radio-group>

      <div class="mode-detail">
        <el-icon><InfoFilled /></el-icon>
        <span>{{ currentMode?.desc }}</span>
      </div>

      <div class="section-actions">
        <el-button
          type="primary"
          :loading="savingMode"
          :disabled="mode === config.newCurlMode"
          @click="handleSaveMode"
        >
          保存模式
        </el-button>
      </div>
    </section>

    <div class="config-grid">
      <section class="config-section sn-section" :class="{ muted: mode !== 'sn' }">
        <div class="section-heading">
          <div>
            <h4>指定 SN 白名单</h4>
            <p>FordServer:V5075:NewCurlSn</p>
          </div>
          <el-tag :type="mode === 'sn' ? 'success' : 'info'" effect="plain">
            {{ mode === 'sn' ? '当前生效' : '当前不生效' }}
          </el-tag>
        </div>

        <div class="sn-toolbar">
          <el-input
            v-model.trim="snInput"
            :placeholder="snWhitelistPlaceholder"
            clearable
            :disabled="snWhitelistDisabled"
            @keyup.enter="handleAddSn"
          >
            <template #append>
              <el-button
                :icon="Plus"
                :loading="addingSn"
                :disabled="snWhitelistDisabled"
                @click="handleAddSn"
              />
            </template>
          </el-input>
          <el-button :icon="Refresh" plain :loading="refreshingSn" @click="fetchSnList">
            刷新
          </el-button>
        </div>

        <el-table
          v-if="snRows.length"
          :data="snRows"
          border
          size="small"
          class="sn-table"
        >
          <el-table-column prop="index" label="#" width="58" align="center" />
          <el-table-column prop="sn" label="SN" min-width="180">
            <template #default="{ row }">
              <span class="mono">{{ row.sn }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="92" align="center">
            <template #default="{ row }">
              <el-popconfirm
                title="确认删除该 SN？"
                confirm-button-text="删除"
                cancel-button-text="取消"
                @confirm="handleDeleteSn(row.sn)"
              >
                <template #reference>
                  <el-button
                    :icon="Delete"
                    type="danger"
                    plain
                    circle
                    size="small"
                    :loading="deletingSn === row.sn"
                  />
                </template>
              </el-popconfirm>
            </template>
          </el-table-column>
        </el-table>

        <el-empty v-else description="暂无 SN" :image-size="76" />
      </section>

      <section class="config-section fallback-section">
        <div class="section-heading">
          <div>
            <h4>Deliver fallback</h4>
            <p>FordServer:V5075:DeliverFallbackV2</p>
          </div>
          <el-tag :type="fallbackEnabled ? 'success' : 'warning'" effect="plain">
            {{ fallbackEnabled ? '开启' : '关闭' }}
          </el-tag>
        </div>

        <div class="fallback-control">
          <el-switch
            v-model="fallbackEnabled"
            inline-prompt
            active-text="开启"
            inactive-text="关闭"
            :disabled="savingFallback"
          />
          <strong>{{ fallbackEnabled ? '未命中时回退到 MyDeliverV2' : '仅使用 MyDeliverV5075' }}</strong>
        </div>

        <div class="risk-note" :class="{ warning: !fallbackEnabled }">
          <el-icon><WarningFilled /></el-icon>
          <span>{{ fallbackNote }}</span>
        </div>

        <div class="section-actions">
          <el-button
            type="primary"
            :loading="savingFallback"
            :disabled="fallbackEnabled === config.deliverFallbackV2"
            @click="handleSaveFallback"
          >
            保存开关
          </el-button>
        </div>
      </section>

    </div>

    <section class="config-section">
      <div class="section-heading">
        <div>
          <h4>操作日志</h4>
          <p>operator / action / oldValue / newValue / time</p>
        </div>
        <el-tag effect="plain">预留</el-tag>
      </div>

      <el-table :data="[]" border size="small" class="audit-table">
        <el-table-column prop="operator" label="operator" min-width="120" />
        <el-table-column prop="action" label="action" min-width="140" />
        <el-table-column prop="key" label="key" min-width="210" />
        <el-table-column prop="oldValue" label="oldValue" min-width="120" />
        <el-table-column prop="newValue" label="newValue" min-width="120" />
        <el-table-column prop="time" label="time" min-width="160" />
      </el-table>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { Delete, InfoFilled, Plus, Refresh, WarningFilled } from '@element-plus/icons-vue';

import {
  addFordV5075NewCurlSn,
  deleteFordV5075NewCurlSn,
  getFordV5075NewCurlSn,
  getFordV5075RedisSwitches,
  setFordV5075DeliverFallbackV2,
  setFordV5075NewCurlMode,
} from '@/api/users';
import {
  canEditV5075SnWhitelist,
  getV5075SnWhitelistPlaceholder,
} from '@/utils/fordV5075Ui.mjs';

const allowedModes = ['off', 'tester', 'all', 'sn'];

const modeOptions = [
  { label: '关闭', value: 'off', desc: '所有接头只走旧结构。' },
  { label: '仅测试接头', value: 'tester', desc: '只对测试接头开放，适合测试阶段默认值。' },
  { label: '全部开放', value: 'all', desc: '对所有 V50.75+ 接头开放。' },
  { label: '指定 SN', value: 'sn', desc: '只对白名单 SN 开放。' },
];

const config = reactive({
  newCurlMode: 'tester',
  newCurlSn: [],
  deliverFallbackV2: true,
  defaults: {
    newCurlMode: 'tester',
    deliverFallbackV2: true,
  },
});

const loading = ref(false);
const savingMode = ref(false);
const savingFallback = ref(false);
const addingSn = ref(false);
const refreshingSn = ref(false);
const deletingSn = ref('');
const mode = ref('tester');
const fallbackEnabled = ref(true);
const snInput = ref('');

const currentMode = computed(() => {
  return modeOptions.find((item) => item.value === mode.value) || modeOptions[1];
});

const canEditSnWhitelist = computed(() => canEditV5075SnWhitelist(mode.value));

const snWhitelistDisabled = computed(() => addingSn.value || !canEditSnWhitelist.value);

const snWhitelistPlaceholder = computed(() => getV5075SnWhitelistPlaceholder(mode.value));

const modeDefaultText = computed(() => {
  return `默认：${config.defaults.newCurlMode || 'tester'}`;
});

const fallbackNote = computed(() => {
  if (fallbackEnabled.value) {
    return '测试阶段建议开启，可降低 V5075 数据缺失造成的失败。';
  }

  return '关闭后只使用 MyDeliverV5075 数据，可用于验证 V5075 数据完整性。';
});

const snRows = computed(() => {
  return config.newCurlSn.map((sn, index) => ({
    index: index + 1,
    sn,
  }));
});

function pickPayload(responseData) {
  if (
    responseData?.data &&
    typeof responseData.data === 'object' &&
    (
      Object.prototype.hasOwnProperty.call(responseData.data, 'newCurlMode') ||
      Object.prototype.hasOwnProperty.call(responseData.data, 'newCurlSn') ||
      Object.prototype.hasOwnProperty.call(responseData.data, 'deliverFallbackV2')
    )
  ) {
    return responseData.data;
  }

  return responseData || {};
}

function normalizeMode(value) {
  const normalized = String(value || '').trim();
  return allowedModes.includes(normalized) ? normalized : 'tester';
}

function normalizeFallback(value, defaultValue = true) {
  if (typeof value === 'boolean') {
    return value;
  }

  const normalized = String(value ?? '').trim().toLowerCase();
  if (['0', 'false', 'off'].includes(normalized)) {
    return false;
  }
  if (['1', 'true', 'on'].includes(normalized)) {
    return true;
  }

  return defaultValue;
}

function normalizeSnList(payload) {
  const candidate = Array.isArray(payload)
    ? payload
    : (
      Array.isArray(payload?.newCurlSn)
        ? payload.newCurlSn
        : (
          Array.isArray(payload?.data)
            ? payload.data
            : (
              Array.isArray(payload?.snList)
                ? payload.snList
                : []
            )
        )
    );

  return [...new Set(candidate.map((sn) => String(sn || '').trim()).filter(Boolean))];
}

function applyConfig(payload) {
  const nextMode = normalizeMode(payload.newCurlMode);
  const nextFallback = normalizeFallback(payload.deliverFallbackV2);

  config.newCurlMode = nextMode;
  config.newCurlSn = normalizeSnList(payload);
  config.deliverFallbackV2 = nextFallback;
  config.defaults = {
    newCurlMode: payload.defaults?.newCurlMode || 'tester',
    deliverFallbackV2: normalizeFallback(payload.defaults?.deliverFallbackV2),
  };

  mode.value = nextMode;
  fallbackEnabled.value = nextFallback;
}

function validateSn(sn) {
  if (!sn) {
    return 'SN 不能为空';
  }

  if (/\s/.test(sn)) {
    return 'SN 不能包含空白字符';
  }

  if (!/^[A-Za-z0-9]{6,32}$/.test(sn)) {
    return 'SN 格式不正确';
  }

  return '';
}

async function fetchConfig() {
  loading.value = true;
  try {
    const response = await getFordV5075RedisSwitches();
    applyConfig(pickPayload(response.data));
  } catch (error) {
    ElMessage.error('获取 V5075 配置失败');
  } finally {
    loading.value = false;
  }
}

async function fetchSnList() {
  refreshingSn.value = true;
  try {
    const response = await getFordV5075NewCurlSn();
    config.newCurlSn = normalizeSnList(response.data);
  } catch (error) {
    ElMessage.error('刷新 SN 白名单失败');
  } finally {
    refreshingSn.value = false;
  }
}

async function handleSaveMode() {
  if (!allowedModes.includes(mode.value)) {
    ElMessage.error('模式值不正确');
    return;
  }

  savingMode.value = true;
  try {
    await setFordV5075NewCurlMode(mode.value);
    config.newCurlMode = mode.value;
    ElMessage.success('模式已保存');
  } catch (error) {
    ElMessage.error('保存模式失败');
  } finally {
    savingMode.value = false;
  }
}

async function handleSaveFallback() {
  savingFallback.value = true;
  try {
    await setFordV5075DeliverFallbackV2(fallbackEnabled.value);
    config.deliverFallbackV2 = fallbackEnabled.value;
    ElMessage.success('fallback 开关已保存');
  } catch (error) {
    ElMessage.error('保存 fallback 开关失败');
  } finally {
    savingFallback.value = false;
  }
}

async function handleAddSn() {
  if (!canEditSnWhitelist.value) {
    ElMessage.warning('请先将新增请求结构模式切换为“指定 SN”');
    return;
  }

  const sn = snInput.value.trim();
  const validationMessage = validateSn(sn);

  if (validationMessage) {
    ElMessage.warning(validationMessage);
    return;
  }

  addingSn.value = true;
  try {
    await addFordV5075NewCurlSn(sn);
    if (!config.newCurlSn.includes(sn)) {
      config.newCurlSn = [sn, ...config.newCurlSn];
    }
    snInput.value = '';
    ElMessage.success('SN 已添加');
  } catch (error) {
    ElMessage.error('添加 SN 失败');
  } finally {
    addingSn.value = false;
  }
}

async function handleDeleteSn(sn) {
  deletingSn.value = sn;
  try {
    await deleteFordV5075NewCurlSn(sn);
    config.newCurlSn = config.newCurlSn.filter((item) => item !== sn);
    ElMessage.success('SN 已删除');
  } catch (error) {
    ElMessage.error('删除 SN 失败');
  } finally {
    deletingSn.value = '';
  }
}

onMounted(() => {
  fetchConfig();
});
</script>

<style scoped>
.v5075-panel {
  display: grid;
  gap: 14px;
  min-width: 0;
}

.panel-header,
.config-section {
  border: 1px solid #dfe8f3;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(31, 56, 88, 0.05);
}

.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
}

.panel-header h3,
.section-heading h4 {
  margin: 0;
  color: #132033;
  font-weight: 800;
  letter-spacing: 0;
}

.panel-header h3 {
  font-size: 18px;
}

.panel-header p,
.section-heading p {
  margin: 5px 0 0;
  color: #6b7b8f;
  font-size: 13px;
  line-height: 1.45;
}

.config-section {
  min-width: 0;
  padding: 16px;
}

.config-section.muted {
  background: linear-gradient(180deg, #ffffff, #f8fafc);
}

.section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.section-heading h4 {
  font-size: 16px;
}

.mode-group {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mode-group :deep(.el-radio-button__inner) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px !important;
  border: 1px solid #d8e5f2;
  box-sizing: border-box;
  min-width: 104px;
  height: 38px;
  line-height: 1;
  padding: 0 14px;
  text-align: center;
  white-space: nowrap;
  box-shadow: none;
  font-weight: 700;
}

.mode-group :deep(.el-radio-button:first-child .el-radio-button__inner),
.mode-group :deep(.el-radio-button:last-child .el-radio-button__inner) {
  border-radius: 8px !important;
}

.mode-detail,
.risk-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #d8e9fb;
  border-radius: 8px;
  color: #416174;
  background: #f2f8ff;
  font-size: 13px;
  line-height: 1.5;
}

.mode-detail .el-icon {
  color: #2563eb;
  flex: 0 0 auto;
}

.section-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}

.config-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(320px, 0.75fr);
  gap: 14px;
  align-items: stretch;
}

.fallback-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.sn-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  margin-bottom: 12px;
}

.sn-table,
.audit-table {
  width: 100%;
}

.sn-table :deep(.el-table__header th),
.audit-table :deep(.el-table__header th) {
  background: #f6f9fc !important;
  color: #4b5f75;
  font-weight: 800;
}

.mono {
  font-family: Consolas, Monaco, monospace;
  color: #132033;
}

.fallback-control {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 42px;
  color: #132033;
}

.fallback-control strong {
  font-size: 14px;
}

.risk-note {
  color: #0f766e;
  background: #ecfdf5;
  border-color: #bbf7d0;
}

.risk-note.warning {
  color: #915a00;
  background: #fff7ed;
  border-color: #fed7aa;
}

.risk-note .el-icon {
  flex: 0 0 auto;
}

@media (max-width: 1100px) {
  .config-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .panel-header,
  .section-heading,
  .sn-toolbar,
  .fallback-control {
    align-items: flex-start;
    grid-template-columns: 1fr;
    flex-direction: column;
  }

  .section-actions {
    justify-content: flex-start;
  }
}
</style>
